import { useEffect, useState } from "react";

import { toArray, userApi } from "../Services";

export interface DirectoryUser {
  name: string;
  email: string;
}

/**
 * The user directory behind the linked Name / Email selects on the Approval
 * Management forms.
 *
 * The /user endpoints do not agree on the email field: the user list answers
 * with `emailAddress` while the approver records use `userEmail`. Both call
 * sites read `emailAddress` alone, so against an endpoint using the other name
 * every entry carried an empty email — the Email select rendered a column of
 * blank options and picking a Name filled in nothing.
 *
 * Every spelling is accepted here, and the two call sites share one
 * implementation so they cannot drift apart again.
 */

const clean = (value: any) => String(value ?? "").trim();

export const useUserDirectory = (source: "admin" | "active" = "admin") => {
  const [users, setUsers] = useState<DirectoryUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    (source === "active" ? userApi.activeUsers() : userApi.adminUsers())
      .then((rows: any) => {
        if (cancelled) return;
        setUsers(
          toArray(rows)
            .map((row: any) => ({
              name: clean(row.userName ?? row.name ?? row.fullName),
              email: clean(row.emailAddress ?? row.userEmail ?? row.email),
            }))
            // An entry with neither half is not selectable in either select.
            .filter((user: DirectoryUser) => user.name || user.email)
        );
      })
      .catch(() => {
        if (!cancelled) setUsers([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [source]);

  /* Each select only offers entries that have the half it displays, so a
     record missing one of them never shows up as a blank option. Each list is
     sorted A–Z on the half it shows. */
  const az = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: "base" });
  const byName = users.filter((user) => user.name).sort((a, b) => az(a.name, b.name));
  const byEmail = users.filter((user) => user.email).sort((a, b) => az(a.email, b.email));

  return { users, byName, byEmail, isLoading };
};
